import { defineComponent, h, nextTick } from 'vue';
import utils from '@/Shared/utils.jsx';

import * as ace from 'ace-builds';
import 'ace-builds/src-noconflict/theme-monokai'; // Import a theme
import 'ace-builds/src-noconflict/theme-github';
import 'ace-builds/src-noconflict/mode-javascript'; // Import a language mode
import 'ace-builds/src-noconflict/mode-csharp';
import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/ext-language_tools';
import 'ace-builds/src-noconflict/ext-searchbox';

import { js as jsBeautify } from 'js-beautify';

import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import mitt from 'mitt';

import { transformDocumentPositionForDelta } from '../vuecontrols/flowv2/shared/helpers.js';

//Vue.component('ace-editor', {
export default defineComponent({
    name: 'AceEditor',
    //template: '<div :id="editorId" style="width: 100%; height: 100%;"></div>',
    emits: ['focus', 'change-content', 'change-delta', 'init', 'breakpoint', 'breakpoints-changed'],
    props: {
        editorId: {
            type: String,
            default: ''
        },
        content: {
            type: String,
            default: ''
        },
        lang: {
            type: String,
            default: 'text'
        },
        theme: {
            type: String,
            default: 'github'
        },
        readOnly: {
            type: Boolean,
            default: false
        },
        wordWrap: {
            type: Boolean,
            default: false
        },
        fontSize: 12
    },
    data() {
        return {
            uniqueid: null,
            _events: null,
            uiAceOptions: {},
            editor: null,
            isEditorMounted: false,
            model: null,
            beforeContent: '',
            hasfocus: false,

            currentExecLine: -1,
            execMarker: null,
        }
    },
    created() {
        this.uniqueid = utils.generateUUID();
        this._events = mitt();
        /** @type {{ line: number, column: number }[]} canonical breakpoints (0-based line); session derives from this */
        this._breakpointList = [];
        /** @type {Map<number, number>} row -> column for persisted getAllBreakpoints */
        this._breakpointColumnByRow = new Map();
        /** @type {Map<string, number>} `${row}:${column}` -> Ace marker id */
        this._columnMarkerIds = new Map();
    },
    methods: {
        gotoLine(row, column) {
            this.editor.gotoLine(row, column, false);
        },
        getCursorOffset() {
            if (!this.editor)
                return null;
            const pos = this.editor.getCursorPosition();
            return this.editor.session.doc.positionToIndex(pos);
        },
        unfoldToPosition(row, column) {
            if (!this.editor)
                return;

            const session = this.editor.session;
            if (!session?.unfold)
                return;

            for (let i = 0; i < 64; i++) {
                const fold = session.getFoldAt(row, column, 1);
                if (!fold)
                    break;
                session.expandFold(fold);
            }

            for (let r = 0; r <= row; r++) {
                if (!session.isRowFolded(r))
                    continue;
                const fold = session.getFoldAt(r, session.getLine(r).length, 1);
                if (fold?.range?.contains(row, column))
                    session.expandFold(fold);
            }

            session.unfold({ row, column }, true);
        },
        setCursorOffset(offset, revealFolds = false) {
            if (!this.editor || offset == null)
                return;
            const pos = this.editor.session.doc.indexToPosition(offset);
            if (revealFolds)
                this.unfoldToPosition(pos.row, pos.column);
            this.editor.moveCursorTo(pos.row, pos.column);
            this.editor.clearSelection();
            this.editor.scrollToLine(pos.row, true, true, () => {});
        },
        beautify(doAppy) {
            utils.log("beautify()");
            let output;

            if (this.lang == "json") { //  this.uiAceOptions.mode == "json") {
                output = JSON.stringify(JSON.parse(this.model), null, "\t");
            }
            else {
                var opts = {
                    indent_size: 1,
                    indent_char: '\t',
                    max_preserve_newlines: 5,
                    preserve_newlines: true,
                    keep_array_indentation: false,
                    break_chained_methods: false,
                    indent_scripts: "normal",
                    brace_style: "expand", // "expand", end-expand, "collapse"
                    space_before_conditional: true,
                    unescape_strings: false,
                    jslint_happy: true,
                    end_with_newline: false,
                    wrap_line_length: "0",
                    indent_inner_html: true,
                    comma_first: false,
                    e4x: false
                };

                if (this.uiAceOptions.mode == "css") {
                    output = this.model; //this.css_beautify(this.model, opts);
                }
                else if (this.looks_like_html(this.model)) {
                    output = this.model; // this.html_beautify(this.model, opts);
                }
                else {
                    //output = this.unpacker_filter(this.model); // detect-packers (optional)
                    //output = this.model; // this.js_beautify(this.model, opts);
                    output = jsBeautify(this.model, opts);
                }
            }

            if (doAppy) // if the CTRL-SHIFT-F is pressed to reformat - it is trapped outside angular and therefore need to be wrapped in $apply
            {
                //Vue.nextTick(function () {
                this.saveEditorPositionAndFoldInfo();
                this.model = output;
                this.editor.setValue(this.model, 1);
                this.restoreEditorPositionAndFoldInfo();
                //});
            }
            else {
                this.model = output;
            }
        },
        saveEditorPositionAndFoldInfo() {
            this.savedFoldData = cloneDeep(this.editor.session.$foldData);
            this.savedScrollTop = this.editor.session.getScrollTop();
            this.savedCursorPosition = this.editor.session.selection.getCursor();
        },
        restoreEditorPositionAndFoldInfo() {
            const c = this;
            nextTick(function () {
                // before restoring the fold data as was, the rows may have changed when reformatting was applied, so we need to verify that the collapsable rows are still collapsable
                let verifiedFoldData = c.verifyFoldData(c.savedFoldData);
                c.addVerifiedFolds(verifiedFoldData);

                c.editor.session.selection.moveCursorTo(c.savedCursorPosition.row, c.savedCursorPosition.column);
                c.editor.session.setScrollTop(c.savedScrollTop);
            });
        },
        verifyFoldData(foldData) {
            let verifiedFoldData = [];

            for (let x = 0; x < foldData.length; x++) {
                let foldRange = foldData[x].range;

                // Validate that the fold range is still good
                let foldWidget = this.editor.session.getFoldWidget(foldRange.start.row);
                if (foldWidget == "start") {
                    let foldWidgetRange = this.editor.session.getFoldWidgetRange(foldRange.start.row);
                    verifiedFoldData.push(foldWidgetRange);
                }
            }

            return verifiedFoldData;
        },
        addVerifiedFolds(verifiedFoldData) {
            for (let x = 0; x < verifiedFoldData.length; x++) {
                this.editor.session.addFold("...", verifiedFoldData[x])
            }
        },
        unpacker_filter(source) {
            let trailing_comments = '',
                comment = '',
                unpacked = '',
                found = false;

            // cut trailing comments
            do {
                found = false;
                if (/^\s*\/\* /.test(source)) {
                    found = true;
                    comment = source.substr(0, source.indexOf('*/') + 2);
                    source = source.substr(comment.length).replace(/^\s+/, '');
                    trailing_comments += comment + "\n";
                }
                else if (/^\s*\/\//.test(source)) {
                    found = true;
                    comment = source.match(/^\s*\/\/.*/)[0];
                    source = source.substr(comment.length).replace(/^\s+/, '');
                    trailing_comments += comment + "\n";
                }
            } while (found);

            let unpackers = [P_A_C_K_E_R, Urlencoded, /*JavascriptObfuscator,*/ MyObfuscate];
            for (let i = 0; i < unpackers.length; i++) {
                if (unpackers[i].detect(source)) {
                    unpacked = unpackers[i].unpack(source);
                    if (unpacked != source) {
                        source = unpacker_filter(unpacked);
                    }
                }
            }

            return trailing_comments + source;
        },
        looks_like_html(source) {
            // <foo> - looks like html
            // <!--\nalert('foo!');\n--> - doesn't look like html

            let trimmed = source.replace(/^[ \t\n\r]+/, '');
            let comment_mark = '<' + '!-' + '-';
            return (trimmed && (trimmed.substring(0, 1) === '<' && trimmed.substring(0, 4) !== comment_mark));
        },

        $on(eventname, method) {
            this._events.on(eventname, method);
        },
        $off(eventname, method) {
            this._events.off(eventname, method);
        },
        $emitself(eventname, args) {
            this._events.emit(eventname, args);
        },

        setEditorFontSize(size) {
            if (this.editor && this.editor.container) {
                this.editor.container.style.fontSize = `${size}px`;
                this.editor.resize(); // Recalculate layout
            }
        },
        /** Soft wrap + Ace default indentedSoftWrap adds extra indent on wrapped rows; turn that off when wrapping. */
        applyWordWrapSessionOptions() {
            if (!this.editor)
                return;
            const session = this.editor.session;
            session.setUseWrapMode(this.wordWrap);
            session.setOption('indentedSoftWrap', !this.wordWrap);
        },
        ensureBreakpointColumnStyles() {
            const id = 'ace-breakpoint-column-marker-style';
            if (document.getElementById(id))
                return;
            const style = document.createElement('style');
            style.id = id;
            style.textContent = `
                .ace_marker-layer .ace_breakpoint-column {
                    position: absolute;
                    pointer-events: none;
                    z-index: 1;
                    background: rgba(255, 60, 60, 0.28);
                    border-bottom: 2px solid rgba(200, 30, 30, 0.9);
                }
            `;
            document.head.appendChild(style);
        },
        _removeColumnMarkerForRow(session, row) {
            const col = this._breakpointColumnByRow.get(row);
            if (col === undefined || col <= 0)
                return;
            const key = `${row}:${col}`;
            const markerId = this._columnMarkerIds.get(key);
            if (markerId != null) {
                session.removeMarker(markerId);
                this._columnMarkerIds.delete(key);
            }
        },
        _clearBreakpointSessionVisuals() {
            if (!this.editor)
                return;
            const session = this.editor.session;
            session.clearBreakpoints();
            for (const markerId of this._columnMarkerIds.values())
                session.removeMarker(markerId);
            this._columnMarkerIds.clear();
            this._breakpointColumnByRow.clear();
        },
        clearAllBreakpointVisuals() {
            this._breakpointList = [];
            this._clearBreakpointSessionVisuals();
        },
        /** One breakpoint per gutter row; sort by line, column */
        _dedupeBreakpoints(list) {
            const byLine = new Map();
            const sorted = [...list].sort((a, b) => a.line - b.line || a.column - b.column);
            for (const p of sorted) {
                if (p.line < 0)
                    continue;
                if (!byLine.has(p.line))
                    byLine.set(p.line, { line: p.line, column: p.column });
            }
            return Array.from(byLine.values()).sort((a, b) => a.line - b.line);
        },
        /** @returns {{ line: number, column: number } | null} */
        _postValidateBreakpointInSession(session, row, col) {
            if (row < 0 || row >= session.getLength())
                return null;
            const lineText = session.getLine(row) || '';
            if (col > 0 && col > lineText.length)
                return null;
            return { line: row, column: col };
        },
        /** Re-apply `_breakpointList` to gutter + column markers (session cleared first). */
        applyBreakpointListToSession(list) {
            if (!this.editor)
                return;
            if (!list || !list.length) {
                this.clearAllBreakpointVisuals();
                return;
            }
            this._breakpointList = this._dedupeBreakpoints(list);
            this._clearBreakpointSessionVisuals();
            for (const bp of this._breakpointList)
                this._applyBreakpointVisualAt(bp.line, bp.column);
            nextTick(() => this.refreshColumnBreakpointMarkers());
        },
        _reconcileBreakpointsOnDelta(delta) {
            if (!this.editor || !this._breakpointList.length)
                return;
            const session = this.editor.session;
            const next = [];
            for (const p of this._breakpointList) {
                const t = transformDocumentPositionForDelta({ line: p.line, column: p.column }, delta);
                if (!t)
                    continue;
                const v = this._postValidateBreakpointInSession(session, t.row, t.column);
                if (v)
                    next.push(v);
            }
            const deduped = this._dedupeBreakpoints(next);
            if (!isEqual(deduped, this._breakpointList)) {
                this._breakpointList = deduped;
                this.applyBreakpointListToSession(this._breakpointList);
                this.$emit('breakpoints-changed');
            }
        },
        _pruneBreakpointsToDocument() {
            if (!this.editor || !this._breakpointList.length)
                return;
            const session = this.editor.session;
            const next = [];
            for (const p of this._breakpointList) {
                const v = this._postValidateBreakpointInSession(session, p.line, p.column);
                if (v)
                    next.push(v);
            }
            const deduped = this._dedupeBreakpoints(next);
            if (!isEqual(deduped, this._breakpointList)) {
                this._breakpointList = deduped;
                this.applyBreakpointListToSession(this._breakpointList);
                this.$emit('breakpoints-changed');
            }
        },
        /** Normalize list entry: number or { line/Line, column/Column } */
        _normalizeBreakpointItem(item) {
            if (typeof item === 'number')
                return { line: item, column: 0 };
            if (item && typeof item === 'object') {
                const line = item.Line ?? item.line;
                const column = item.Column ?? item.column ?? 0;
                return { line: Number(line), column: Number(column) || 0 };
            }
            return { line: 0, column: 0 };
        },
        /**
         * Rebuild column-span markers from _breakpointColumnByRow after the document changes.
         * Without this, markers created while the session was shorter are drawn at clipped
         * document positions (often row 0, col 0) because documentToScreenPosition clamps rows.
         */
        refreshColumnBreakpointMarkers() {
            if (!this.editor)
                return;
            const session = this.editor.session;
            const breaks = session.getBreakpoints();
            for (const markerId of this._columnMarkerIds.values())
                session.removeMarker(markerId);
            this._columnMarkerIds.clear();
            for (const [row, column] of this._breakpointColumnByRow.entries()) {
                if (!breaks[row] || column <= 0)
                    continue;
                this._placeColumnBreakpointMarker(session, row, column);
            }
        },
        _placeColumnBreakpointMarker(session, row, column) {
            if (column <= 0)
                return;
            const line = session.getLine(row) || '';
            if (!line.length)
                return;
            const startCol = Math.min(column, Math.max(0, line.length - 1));
            const endCol = Math.min(column + 1, line.length);
            if (endCol <= startCol)
                return;
            const Range = ace.require('ace/range').Range;
            const range = new Range(row, startCol, row, endCol);
            // inFront must be true: back markers sit under the text canvas and are not visible.
            const id = session.addMarker(range, 'ace_breakpoint-column', 'text', true);
            this._columnMarkerIds.set(`${row}:${column}`, id);
        },
        /** Session + maps only; does not mutate `_breakpointList`. */
        _applyBreakpointVisualAt(row, column = 0) {
            if (!this.editor)
                return;
            const session = this.editor.session;
            this._removeColumnMarkerForRow(session, row);
            session.setBreakpoint(row);
            this._breakpointColumnByRow.set(row, column);
            if (column <= 0)
                return;
            this._placeColumnBreakpointMarker(session, row, column);
        },
        setBreakpointAt(row, column = 0) {
            if (!this.editor)
                return;
            const merged = this._breakpointList.filter(b => b.line !== row).concat([{ line: row, column }]);
            this.applyBreakpointListToSession(merged);
        },
        clearBreakpointAt(row) {
            if (!this.editor)
                return;
            const merged = this._breakpointList.filter(b => b.line !== row);
            this.applyBreakpointListToSession(merged);
        },
        addBreakpointSupport() {
            this.editor.on('guttermousedown', (e) => {
                const target = e.domEvent.target;
                if (target.className.indexOf('ace_gutter-cell') === -1) return;
                if (e.clientX > 40 + target.getBoundingClientRect().left) return;

                const row = e.getDocumentPosition().row;
                const breaks = this.editor.session.getBreakpoints();

                if (breaks[row]) {
                    this.$emit('breakpoint', row, false);
                } else {
                    this.$emit('breakpoint', row, true);
                }

                e.stop();
            });
        },
        setBreakpoint(row) {
            this.setBreakpointAt(row, 0);
        },
        clearBreakpoint(row) {
            this.clearBreakpointAt(row);
        },
        getAllBreakpoints() {
            return this._breakpointList.map(b => ({ line: b.line, column: b.column }));
        },
        /** @param {number[]|{line?:number,Line?:number,column?:number,Column?:number}[]} items */
        setBreakpoints(items) {
            if (!this.editor)
                return;
            if (!items || !items.length) {
                this.clearAllBreakpointVisuals();
                return;
            }
            const normalized = [];
            for (const item of items) {
                const { line, column } = this._normalizeBreakpointItem(item);
                if (line >= 0)
                    normalized.push({ line, column });
            }
            if (!normalized.length) {
                this.clearAllBreakpointVisuals();
                return;
            }
            this.applyBreakpointListToSession(normalized);
        },
        /** @param {number} lineNumber 1-based document line */
        setExecutionPoint(lineNumber, column) {
            if (!this.editor)
                return;
            const session = this.editor.getSession();

            if (this.execMarker != null) {
                session.removeMarker(this.execMarker);
                this.execMarker = null;
            }

            if (this.currentExecLine > 0)
                session.removeGutterDecoration(this.currentExecLine - 1, "ace_execution-point");

            this.currentExecLine = lineNumber;

            session.addGutterDecoration(lineNumber - 1, "ace_execution-point");

            const row = lineNumber - 1;
            const col = column != null ? Number(column) : 0;

            if (col > 0) {
                const lineText = session.getLine(row) || '';
                if (lineText.length) {
                    const startCol = Math.min(col, Math.max(0, lineText.length - 1));
                    const endCol = Math.min(col + 1, lineText.length);
                    if (endCol > startCol) {
                        const Range = ace.require('ace/range').Range;
                        const range = new Range(row, startCol, row, endCol);
                        this.execMarker = session.addMarker(range, 'ace_execution-column', 'text', true);
                    }
                }
            }

            this.editor.scrollToLine(row, true, true);
            if (this.execMarker != null)
                this.editor.renderer.scrollCursorIntoView({ row, column: col }, 0.5);
        },

        clearExecutionPoint() {
            if (!this.editor)
                return;
            const session = this.editor.getSession();

            if (this.execMarker != null) {
                session.removeMarker(this.execMarker);
                this.execMarker = null;
            }

            if (this.currentExecLine > 0)
                session.removeGutterDecoration(this.currentExecLine - 1, "ace_execution-point");

            this.currentExecLine = -1;
        },

        getContent() {
            return this.editor.getValue();
        },
    },
    watch: {
        fontSize(value) {
            this.setEditorFontSize(value);
        },
        wordWrap() {
            this.applyWordWrapSessionOptions();
        },
        content(value) {
            if (!this.editor) {
                this.beforeContent = value;
                return;
            }
            if (this.beforeContent === value)
                return;
            try {
                const next = JSON.parse(value || 'null');
                const prev = JSON.parse(this.beforeContent || 'null');
                if (typeof next === 'object' && next !== null && typeof prev === 'object' && prev !== null && isEqual(next, prev)) {
                    this.beforeContent = value;
                    return;
                }
            }
            catch (e) {
                /* not both JSON values; fall through to setValue */
            }
            this.editor.setValue(value, 1);
            this.beforeContent = value;
            nextTick(() => {
                this.refreshColumnBreakpointMarkers();
                this._pruneBreakpointsToDocument();
            });
        }
    },
    computed: {
        realId() {
            if (this.editorId)
                return this.editorId + '_' + this.uniqueid;

            return 'Editor_' + this.uniqueid;
        },
    },
    render() {
        // <button title="Resizes the editor according to the current width and height" style={{ fontSize: "x-small" }} on-click={(e) => this.editor.resize()}>Resize</button>
        return (
            <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", minHeight: 'inherit', minWidth: 'inherit' }}>
                <div id={this.realId} ref="editorHost" style={{ flexGrow: "1" }}>
                </div>
            </div>
        );
    },
    async mounted() {
        this.model = this.content || '';

        // Set the basePath for Ace Editor
        // ace.config.set('basePath', '/node_modules/ace-builds/src-noconflict');

        await nextTick();

        const host = this.$refs.editorHost;
        if (!host || !host.isConnected) {
            utils.warn(`AceEditor mount skipped; host element not available for ${this.realId}`);
            return;
        }

        const readOnly = this.readOnly;
        this.editor = ace.edit(host, {
            theme: `ace/theme/${this.theme}`,
            mode: `ace/mode/${this.lang}`,
            enableBasicAutocompletion: !readOnly,
            enableLiveAutocompletion: !readOnly,
            enableSnippets: !readOnly,
            fontSize: '12px',
        });
        this.isEditorMounted = true;
        this.editor.setValue(this.model, 1);
        this.editor.container.style.fontSize = `${this.fontSize}px`;

        // mode-xxx.js or theme-xxx.jsがある場合のみ有効
        //this.editor.getSession().setMode(`ace/mode/${this.lang}`);
        //this.editor.setTheme(`ace/theme/${this.theme}`);
        this.editor.setAutoScrollEditorIntoView(!readOnly);
        this.editor.setReadOnly(readOnly);

        // Disable linting to remove warnings on top level await (used in FastGrid)
        this.editor.session.setOption("useWorker", false);
        this.applyWordWrapSessionOptions();
        this.ensureBreakpointColumnStyles();

        if (typeof ResizeObserver !== 'undefined') {
            this._hostResizeObserver = new ResizeObserver(() => {
                this.editor?.resize?.();
            });
            this._hostResizeObserver.observe(host);
        }

        setTimeout(() => {
            if (!this.editor)
                return;
            //this.editor.moveCursorTo(1, 1);
            this.editor.session.selection.moveCursorTo(0, 0);
            this.editor.session.setScrollTop(1);
        }, 500);

        this.editor.on('focus', () => {
            this.hasfocus = true;
            this.$emit('focus', this.editor.getValue());
        });

        this.editor.on('blur', () => {
            this.hasfocus = false;
            const v = this.editor.getValue();
            this.beforeContent = v;
            if (v !== this.content)
                this.$emit('change-content', v);
        });

        this.editor.on('change', (delta) => {
            this._reconcileBreakpointsOnDelta(delta);
            this.$emit('change-delta', delta);
            this.model = this.editor.getValue();
        });

        this.$on('Beautify', this.beautify);

        const c = this;
        this.editor.commands.addCommands([
            {
                name: 'formatDocument',
                bindKey: {
                    win: 'Ctrl-Shift-F',
                    mac: 'Command-Shift-F'
                },
                exec: function () {
                    c.$emitself('Beautify', true);
                    return false;
                },
                readOnly: true
            }
        ]);

        this.$emit('init', this.editor);
    },
    beforeUnmount() {
        this.$off('Beautify', this.beautify);
        if (this._hostResizeObserver) {
            this._hostResizeObserver.disconnect();
            this._hostResizeObserver = null;
        }
        if (this.editor) {
            this.clearAllBreakpointVisuals();
            this.editor.destroy();
            this.editor = null;
        }
        this.isEditorMounted = false;
    }
});